import React from 'react';

import { useDispatch } from 'react-redux';
import { HN_SKJEMA_FRONTEND_RESOURCES } from 'src/constants/constants';
import { useGetSubmitButtonText } from 'src/hooks/useGetSubmitButtonText';
import { useResources } from 'src/resources';
import { useAppSelector } from 'src/store/reducers';
import { getIsSavingDisabled } from 'src/store/reducers/selectors';

import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { HNSkjemaFrontend } from 'src/types/Resources';

import Button from '@helsenorge/designsystem-react/components/Button';
import Icon from '@helsenorge/designsystem-react/components/Icon';
import Save from '@helsenorge/designsystem-react/components/Icons/Save';
import StepButtons from '@helsenorge/designsystem-react/components/StepButtons';

import { erTjenester } from '@helsenorge/framework-utils/hn-proxy-service';
import { generateQuestionnaireResponse, setSkjemaDefinitionAction } from '@helsenorge/refero';

import { CustomFormButtonType } from './useCustomFormButtonType';

interface Props {
  formButtonType: CustomFormButtonType;
  reactHookFormMethods: UseFormReturn<FieldValues, unknown>;
  onCancel: () => void;
  onSubmit: () => void;
  trackFormError: () => void;
  erSendOgLagreKnapperDisabled: boolean;
  nextStep: (() => void) | undefined;
  previousStep: (() => void) | undefined;
  displayNextButton: boolean | undefined;
  displayPreviousButton: boolean | undefined;
  setNewStepIndex?: (index: number) => void;
  onSave?: () => void;
}

const FormButtonsStandardStepView = ({
  formButtonType,
  reactHookFormMethods,
  onCancel,
  onSubmit,
  trackFormError,
  erSendOgLagreKnapperDisabled,
  nextStep,
  previousStep,
  displayNextButton,
  displayPreviousButton,
  setNewStepIndex,
  onSave,
}: Props): React.JSX.Element => {
  const resources = useResources<HNSkjemaFrontend>(HN_SKJEMA_FRONTEND_RESOURCES);
  const formDefinition = useAppSelector(state => state.refero.form.FormDefinition);
  const kanMellomlagres = useAppSelector(state => state.refero.ui.kanMellomlagres);
  const isSaveDisabled = useAppSelector(getIsSavingDisabled);
  const isMicroweb = useAppSelector(state => state.microweb.isMicroweb);
  const isMicrowebOppgave = useAppSelector(state => state.microweb.isMicrowebOppgave);
  const isMicrowebStep = useAppSelector(state => state.microweb.isMicrowebStep);

  const hasGuidanceAction = useAppSelector(state => state.refero.ui.harGuidanceAction);
  const { hidebackbutton } = useAppSelector(state => state.microweb);
  const submitButtonText = useGetSubmitButtonText();
  const dispatch = useDispatch();
  const onSubmitButtonClicked = (): void => {
    const handleSubmit = reactHookFormMethods.handleSubmit(
      () => onSubmit(),
      () => trackFormError()
    );
    handleSubmit();
  };

  const nextStepClicked = (): void => {
    if (nextStep) {
      const handleSubmit = reactHookFormMethods.handleSubmit(
        () => nextStep(),
        () => trackFormError()
      );
      handleSubmit();
    }
  };
  const handleCancel = (): void => {
    if (formButtonType === CustomFormButtonType.microwebStandardStepView) {
      if (formDefinition.Content) {
        dispatch(
          setSkjemaDefinitionAction({
            questionnaire: formDefinition.Content,
            questionnaireResponse: generateQuestionnaireResponse(formDefinition.Content),
            syncQuestionnaireResponse: true,
          })
        );
        setNewStepIndex?.(0);
      }
    } else {
      onCancel();
    }
  };
  const handleSave = (): void => {
    onSave?.();
  };

  const shouldHideSubmitButton =
    isSaveDisabled && !hasGuidanceAction && !erTjenester() && isMicroweb && !isMicrowebStep && !isMicrowebOppgave;

  const submitButton = shouldHideSubmitButton ? undefined : (
    <Button disabled={erSendOgLagreKnapperDisabled} onClick={onSubmitButtonClicked} testId="submit-button">
      {submitButtonText}
    </Button>
  );

  return (
    <div className="formButtonsBackground" data-testid="form-buttons-standard-step-view">
      <StepButtons
        backButton={
          !hidebackbutton && displayPreviousButton ? (
            <Button onClick={previousStep} testId="previousStep-button">
              {resources?.microwebstep_navigasjon_tilbake_button}
            </Button>
          ) : undefined
        }
        cancelButton={
          <Button onClick={handleCancel} testId="cancel-button">
            {resources?.microwebstep_navigasjon_avbryt_button}
          </Button>
        }
        forwardButton={
          displayNextButton ? (
            <Button disabled={erSendOgLagreKnapperDisabled} onClick={nextStepClicked} testId="nextStep-button">
              {resources?.microwebstep_navigasjon_neste_button}
            </Button>
          ) : (
            submitButton
          )
        }
        additionalButtons={
          kanMellomlagres
            ? [
                <Button key={1} variant="outline" disabled={erSendOgLagreKnapperDisabled} onClick={handleSave} testId="save-button">
                  <Icon svgIcon={Save} />
                  {resources?.formSave}
                </Button>,
              ]
            : []
        }
      />
    </div>
  );
};

export default FormButtonsStandardStepView;
